import React, { useState, useEffect } from 'react';
import { Copy, Check, Eye, EyeOff } from 'lucide-react';
import { tenantApi } from '../lib/tenant-api';

interface WidgetConfigurationScriptProps {
  apiKey: string;
  tenantName: string;
  isKeyVisible?: boolean;
}

const generateWidgetScript = (apiKey: string, includeAction: boolean = false, actionId: string = 'action-id'): string => {
  const widgetScriptUrl = import.meta.env.VITE_WIDGET_SCRIPT_URL || 'https://storelawmaprodwestus2001.z5.web.core.windows.net/widget.js';

  const actionScript = includeAction ? `
       LAWMAai.registerAction('${actionId}', () => {
          // call some function on action button click
          // callSomeFunction();
       });` : '';

  return `<script src="${widgetScriptUrl}"></script>
<script>
    document.addEventListener('DOMContentLoaded', function() {
        LAWMAai.init({
            apiKey: '${apiKey}',
        });${actionScript}
    });
</script>`;
};


export const WidgetConfigurationScript: React.FC<WidgetConfigurationScriptProps> = ({
  apiKey,
  tenantName,
  isKeyVisible = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(isKeyVisible);
  const [includeActionButton, setIncludeActionButton] = useState(false);
  const [selectedActionCTA, setSelectedActionCTA] = useState<string>('');
  const [availableCTAs, setAvailableCTAs] = useState<Array<{ id: string; name: string; cta_type: string }>>([]);

  useEffect(() => {
    const fetchCTAs = async () => {
      try {
        const ctas = await tenantApi.getCTAs();
        setAvailableCTAs(ctas.map(cta => ({
          id: cta.id,
          name: cta.name,
          cta_type: cta.cta_type
        })));
      } catch (err) {
        console.error('Failed to fetch CTAs:', err);
      }
    };

    fetchCTAs();
  }, [tenantName]);

  const maskApiKey = (key: string) => {
    if (!key) return '';
    if (key === 'YOUR_API_KEY_HERE') {
      return 'YOUR_API_KEY_HERE';
    }
    return `sk_...${key.slice(-4)}`;
  };

  const displayApiKey = showKey ? apiKey : maskApiKey(apiKey);
  const actionId = selectedActionCTA || 'action-id';
  const script = generateWidgetScript(displayApiKey, includeActionButton, actionId);

  const handleCopy = async () => {
    try {
      const fullScript = generateWidgetScript(apiKey, includeActionButton, actionId);
      await navigator.clipboard.writeText(fullScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy script:', err);
    }
  };

  const toggleKeyVisibility = () => {
    setShowKey(!showKey);
  };

  return (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-200  p-4">
        <h3 className="text-lg font-semibold text-indigo-900 mb-2">
          Widget Integration Script
        </h3>
        <p className="text-sm text-indigo-800 mb-4">
          Copy this script and paste it at the <code className="bg-indigo-100 px-1 ">&lt;head&gt;</code> section of your website's HTML.
        </p>

        <div className="relative">
          <div className="bg-gray-900  overflow-hidden">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-400 text-xs ml-3 font-mono">widget-integration.html</span>
              </div>
            </div>
            <pre className="p-4 text-sm overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed text-gray-100">
              {script}
            </pre>
          </div>

          <div className="absolute top-12 right-2 flex space-x-2">
            <button
              onClick={toggleKeyVisibility}
              className="p-2 bg-gray-700 text-gray-300  hover:bg-gray-600 transition-colors"
              title={showKey ? "Hide API Key" : "Show API Key"}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>

            <button
              onClick={handleCopy}
              className="p-2 bg-indigo-600 text-white  hover:bg-indigo-700 transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {copied && (
          <div className="mt-2 text-sm text-green-600 font-medium">
            ✓ Script copied to clipboard!
          </div>
        )}
      </div>

      <div className="bg-green-50 border border-green-200  p-4">
        <h4 className="font-semibold text-green-900 mb-3">
          Action Button Integration
        </h4>

        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="include-action-button"
              checked={includeActionButton}
              onChange={(e) => setIncludeActionButton(e.target.checked)}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 "
            />
            <label htmlFor="include-action-button" className="ml-2 block text-sm font-medium text-gray-900">
              Include action button
            </label>
          </div>

          {includeActionButton && (
            <div className="ml-6 space-y-2">
              <label htmlFor="cta-selector" className="block text-sm font-medium text-gray-700">
                Select CTA (optional)
              </label>
              <select
                id="cta-selector"
                value={selectedActionCTA}
                onChange={(e) => setSelectedActionCTA(e.target.value)}
                className="block w-full border border-gray-300  px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Use placeholder 'action-id'</option>
                {availableCTAs.map((cta) => (
                  <option key={cta.id} value={cta.id}>
                    {cta.name} ({cta.cta_type})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-600">
                {selectedActionCTA
                  ? 'The selected CTA\'s ID will be used in the script'
                  : 'Leave empty to use the placeholder "action-id" - you can copy the actual ID from the CTA management page'}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200  p-4">
        <h4 className="font-semibold text-yellow-900 mb-2">
          Integration Instructions
        </h4>
        <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
          <li>Copy the script above by clicking the copy button</li>
          <li>Open your website's main HTML file (usually <code className="bg-yellow-100 px-1 ">index.html</code>)</li>
          <li>Paste the script at the <code className="bg-yellow-100 px-1 ">&lt;head&gt;</code> section</li>
          <li>Save the file and refresh your website</li>
          <li>The AI chat widget will appear on your website</li>
        </ol>
      </div>

      {!isKeyVisible && (
        <div className="bg-red-50 border border-red-200  p-4">
          <h4 className="font-semibold text-red-900 mb-2">
            ⚠️ Security Notice
          </h4>
          <p className="text-sm text-red-800">
            {apiKey === 'YOUR_API_KEY_HERE' ? (
              'For security reasons, API keys are not stored. Replace "YOUR_API_KEY_HERE" in the script above with your actual API key before using it on your website.'
            ) : (
              'For security reasons, your API key is masked. Click the eye icon to reveal it temporarily, or copy the script above (which includes the full key) for integration.'
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default WidgetConfigurationScript;
